import React, { useState, useEffect, useContext } from 'react';
import { Box, Tooltip } from '@mui/material';
import SaveAltRoundedIcon from '@mui/icons-material/SaveAltRounded';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import CommonAccordion from "../../custom-components/CommonAccordion";
import CommonDownload from '../../custom-components/CommonDownload';
import CodeEditor from './CodeEditor';
import CommonCopy from "../../custom-components/CommonCopy";
import { TestngJavaWithToolCalling } from '../../../services/webServices';
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { LoadingModalContext } from "../../main-pages/ServicePage";
import SuccessPopup from "./SuccessPopup";

const TestMethods = ({ testMethods,nextButton }) => {
    const [downloadOpen, setDownloadOpen] = useState(false);
    const [activeThumb, setActiveThumb] = useState(null);
    const [processedTestMethods, setProcessedTestMethods] = useState('');
    const [popupOpen, setPopupOpen] = useState(false);
    const [popupMessage, setPopupMessage] = useState("");
    const [zipBlob, setZipBlob] = useState(null);
    const [openSuccessPopup, setOpenSuccessPopup] = useState(false); // State to control the SuccessPopup
    const { setLoadingState } = useContext(LoadingModalContext);
    const [testEnvironmentData, setTestEnvironmentData] = useState(
        // props.testEnvironmentData || {}
        testMethods.testEnvironmentData || {}
    );

    const defaultExpanded = testMethods.TestMethods !== "";

    const pageClass = testMethods.pageClass;
    // Process test methods for display
    useEffect(() => {
        const processed = (() => {
            try {
                if (Array.isArray(testMethods)) {
                    return testMethods.map(tm => Object.values(tm).join('\n')).join('\n\n');
                }
                if (typeof testMethods === 'string') return testMethods;
                return JSON.stringify(testMethods, null, 2);
            } catch (e) {
                console.error("Error processing testMethods:", e);
                return testMethods;
            }
        })();
        setProcessedTestMethods(processed);
    }, [testMethods]);

    const handleMavenProject = async () => {
  let fake;

  const startProgress = (setProgress) => {
    let progress = 0;
    let stop = false;
    let finalMessageShown = false;

    const interval = setInterval(() => {
      if (stop) return;

      progress += 0.001;
      if (progress >= 98 && !finalMessageShown) {
        finalMessageShown = true;
        setLoadingState((prev) => ({
          ...prev,
          message: "Almost done! Fetching your output",
        }));
      }

      if (progress >= 98) progress = 98;

      setProgress((prev) => ({
        ...prev,
        progress: Math.max(prev.progress, Math.floor(progress)),
      }));
    }, 200);

    return {
      stop: () => {
        stop = true;
        clearInterval(interval);
      },
      completeTo100: async () => {
        stop = true;
        clearInterval(interval);
        setProgress((prev) => ({ ...prev, progress: 100 }));
        await new Promise((res) => setTimeout(res, 500));
      },
    };
  };

  // Initial loading state
  setLoadingState({ open: true, message: "Generating your Project", progress: 0 });
  fake = startProgress(setLoadingState);

  try {
    const response = await TestngJavaWithToolCalling({ pageClassData: pageClass, testmethodsData: testMethods }, testMethods.framework);

    const {
      pomXmlContent,
      testngSuiteFile,
      testRunnerClassContent,
      configProperties,
      extentProperties,
      loggingConfig,
      supportClasses,
      readmeFile,
    } = response.data;

    const zip = new JSZip();

    // Add pom.xml
    if (response.data.testProfileContent) {
      const profileContent = response.data.testProfileContent;
      zip.file("pom.xml", pomXmlContent.replace(/<\/build>/, `</build>\n${profileContent}`));
    } else {
      zip.file("pom.xml", pomXmlContent);
    }

    // Add testng.xml suite file
    if (testngSuiteFile) zip.file("testng.xml", testngSuiteFile);

    // Add README
    if (readmeFile) zip.file("README.md", readmeFile);

    // Add TestRunner
    if (testRunnerClassContent) zip.file("src/test/java/runners/TestRunner.java", testRunnerClassContent);

    // Add config.properties
    const generateConfigFile = (data) => `
url=${data.url || ""}
browser=${data.browser || ""}
os=${data.os || ""}
username=${data.username || ""}
password=${data.password || ""}
    `.trim();

    zip.file("src/main/resources/config.properties", generateConfigFile(testEnvironmentData));

    // Add page objects
    pageClass.forEach((page) => {
      const classname = Object.keys(page)[0];
      const classcontent = page[classname];
      zip.file(`src/main/java/pageobjects/${classname}.java`, classcontent);
    });

    // Add test classes
    testMethods.forEach((test) => {
      const classname = Object.keys(test)[0];
      const classcontent = test[classname];
      zip.file(`src/test/java/tests/${classname}.java`, classcontent);
    });

    // Add utils folder
    const utilsFolder = zip.folder("src/main/java/utils");
    utilsFolder.file("DriverManager.java", "");
    utilsFolder.file("ConfigReader.java", "");
    utilsFolder.file("TestDataUtil.java", "");

    // Add config folder
    const configFolder = zip.folder("src/main/java/config");
    configFolder.file("BrowserFactory.java", "");
    configFolder.file("Constants.java", "");

    // Add testdata.json
    zip.file("src/main/resources/testdata.json", JSON.stringify(testEnvironmentData, null, 2));

    // Generate ZIP and save
    zip.generateAsync({ type: "blob" }).then((content) => {
      setZipBlob(content);
      saveAs(content, "Selenium_TestNG_Project.zip");
    });

    // Complete progress and close loader
    await fake.completeTo100();
    setLoadingState({ open: false, message: "", progress: null });
    setOpenSuccessPopup(true);

  } catch (error) {
    const errorMsg = error?.response?.data?.Error || "An error occurred. Please try again.";
    setPopupMessage(errorMsg);
    setPopupOpen(true);
    setLoadingState({ open: false, message: "", progress: null });
  }
};


    const handleDownloadClick = () => setDownloadOpen(true);
    const handleCloseDownload = () => setDownloadOpen(false);

    const handleThumbClick = (thumbType) => {
        setActiveThumb(prev => (prev === thumbType ? null : thumbType));
    };

    const buttonGroup = [
        { label: 'Download', onClick: handleDownloadClick, variant: 'text', startIcon: <SaveAltRoundedIcon />, sx: { textTransform: 'none' } },
        ...(nextButton || [])
    ];
    const [expanded, setExpanded] = useState(true);

    return (
        <Box>
            <CommonAccordion
                summary={"Test Methods"}
                details={
                    <Box>
                        <CodeEditor 
                            codeContent={processedTestMethods} 
                            setCodeContent={() => {}} 
                            readOnly={true} 
                        />
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, mt: 2 }}>
                            <Tooltip title="Like" arrow>
                                <Box onClick={() => handleThumbClick("like")} sx={{ cursor: "pointer", mr: 2, "&:hover": { color: "#49A3FF" } }}>
                                    {activeThumb === "like" ? <ThumbUpIcon sx={{ fontSize: 24 }} /> : <ThumbUpOutlinedIcon sx={{ fontSize: 24 }} />}
                                </Box>
                            </Tooltip>
                            <Tooltip title="Dislike" arrow>
                                <Box onClick={() => handleThumbClick("dislike")} sx={{ cursor: "pointer", mr: 2, "&:hover": { color: "#FF4842" } }}>
                                    {activeThumb === "dislike" ? <ThumbDownIcon sx={{ fontSize: 24 }} /> : <ThumbDownOutlinedIcon sx={{ fontSize: 24 }} />}
                                </Box>
                            </Tooltip>
                            <CommonCopy contentToCopy={processedTestMethods} />
                        </Box>
                    </Box>
                }
                buttons={buttonGroup}
                fullScreenMode={true}
                // defaultExpanded={true} // opens by default 
                defaultExpanded={defaultExpanded}
                type={"output"}
                expanded={expanded}                   // controlled
                onChange={() => setExpanded(!expanded)} // toggle
            />
            <CommonDownload 
                open={downloadOpen} 
                onClose={handleCloseDownload}
                serviceResponse={[{ heading: "Test Methods", type: "text", data: processedTestMethods }]}
                serviceName="Test Methods"
                wordData={[{ heading: "Test Methods", type: "text", data: processedTestMethods }]}
                documents={['docx']}
            />
            <SuccessPopup
                open={openSuccessPopup}
                handleClose={() => setOpenSuccessPopup(false)}
                // text={"Maven"}
            />
        </Box>
    );
};

export default TestMethods;
